import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get, post, put, remove } from "../../../utils/apiUtils";

/* ------------------------------- Types ----------------------------------- */

export type FieldType = "text" | "number" | "date" | "option_list";

export type DescriptorSet = { id: string; name: string; project_id: string };

export type DescriptorField = {
  id: string;
  name: string;
  type: FieldType | string;
  is_dynamic: boolean;
  descriptor_set_id: string;
  /** Opsionet me ID (vetëm për option_list) */
  optionsFull?: Array<{ id: string; option: string }>;
};

export type DescriptorRow = {
  id: string;
  weight?: number | null;
  is_promoted?: boolean;
  promotion_id?: string | null;
  linked_media?: number;
  media_titles?: string;
  values: Record<string, any>;
};

export type MediaRow = {
  id: string;
  linkedMediaId?: string | null;
  username: string;
  documentName: string;
  documentType?: "Internal" | "External" | string;
  codes: number;
  date: string;
  highlightLength: number;
  createdBy: string;
  modifiedBy: string;
};

type SetsState = {
  items: DescriptorSet[];
  total: number;
  page: number;
  pageSize: number;
  search: string;
  loading: boolean;
};
type FieldsState = {
  items: DescriptorField[];
  loading: boolean;
  editingDetail: any | null;
};
type DescState = {
  items: DescriptorRow[];
  total: number;
  page: number;
  pageSize: number;
  search: string;
  loading: boolean;
};

type Paged<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
};

type LinkedMediaModalState = {
  open: boolean;
  descriptorId: string | null;
  projectId?: string | null;
  descriptorLabel?: string | null;
  tab: "linked" | "unlinked";
  linked: Paged<MediaRow>;
  unlinked: Paged<MediaRow>;
  mutatingId: string | null; // <— was: mutating: boolean
};

type ModalsState = {
  setOpen: boolean;
  setMode: "add" | "edit";
  editingSet: DescriptorSet | null;

  fieldOpen: boolean;
  fieldMode: "add" | "edit";
  editingField: DescriptorField | null;
  fieldInitialValues: {
    name?: string;
    type?: FieldType;
    is_dynamic?: boolean;
    description?: string;
    options?: string[];
  } | null;

  deleteSetOpen: boolean;
  deletingSet: DescriptorSet | null;

  deleteFieldOpen: boolean;
  deletingField: { id: string; name: string } | null;

  // Descriptor values modal
  descriptorOpen: boolean;
  descriptorMode: "add" | "edit";
  editingDescriptor: DescriptorRow | null;

  // NEW: delete descriptor modal
  deleteDescriptorOpen: boolean;
  deletingDescriptor: { id: string; title?: string } | null;

  // Linked Media modal
  linkedMedia: LinkedMediaModalState;
  descriptorInitialValues?: Record<string, any> | null;
  descriptorInitialLabel?: string | null;
};

export type DescriptorsSliceState = {
  projectId: string | null;
  selectedSetId: string | null;
  submitting: boolean;

  sets: SetsState;
  fields: FieldsState;
  descriptors: DescState;

  modals: ModalsState;
};

/* -------------------------------- Helpers -------------------------------- */

const normalizeOptions = (raw: any): string[] | undefined => {
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    return raw
      .map((it) => (typeof it === "string" ? it : (it?.option ?? it?.value)))
      .filter(Boolean);
  }
  return undefined;
};

// Ndarja për update: ekzistuese (me id) vs të reja (pa id)
const splitOptionsForUpdate = (
  current: string[] | undefined,
  previous?: Array<{ id?: string; option?: string }>,
): {
  keepOrUpdate: Array<{ id: string; option: string }>;
  newOnes: string[];
} => {
  const keepOrUpdate: Array<{ id: string; option: string }> = [];
  const newOnes: string[] = [];
  if (!current) return { keepOrUpdate, newOnes };

  const prevMap = new Map(
    (previous || [])
      .filter((o) => o && o.option)
      .map((o) => [String(o.option).trim(), String(o.id || "")]),
  );

  for (const raw of current.map((s) => s?.trim()).filter(Boolean)) {
    const id = prevMap.get(raw);
    if (id) keepOrUpdate.push({ id, option: raw });
    else newOnes.push(raw);
  }
  return { keepOrUpdate, newOnes };
};

/* -------------------------------- Thunks --------------------------------- */
/* ----------------------- Sets ----------------------- */

export const fetchSets = createAsyncThunk(
  "descriptors/fetchSets",
  async (_: void, { getState }) => {
    const state = getState() as { descriptors: DescriptorsSliceState };
    const { projectId, sets } = state.descriptors;
    if (!projectId) throw new Error("Missing project id");
    const res = await get<{ items: DescriptorSet[]; total: number }>(
      "descriptor-sets",
      {
        params: {
          skip: (sets.page - 1) * sets.pageSize,
          limit: sets.pageSize,
          project_id: projectId,
          name: sets.search || undefined,
        },
      },
    );
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    return res.data;
  },
);

export const createSet = createAsyncThunk(
  "descriptors/createSet",
  async (title: string, { getState, dispatch }) => {
    const { projectId } = (getState() as any)
      .descriptors as DescriptorsSliceState;
    if (!projectId) throw new Error("Missing project id");
    const payload = { name: title.trim(), project_id: projectId };
    const res = await post<any>("descriptor-set/create", payload, {});
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    await dispatch(fetchSets());
    return res.data;
  },
);

export const updateSet = createAsyncThunk(
  "descriptors/updateSet",
  async (
    { id, title }: { id: string; title: string },
    { getState, dispatch },
  ) => {
    const { projectId } = (getState() as any)
      .descriptors as DescriptorsSliceState;
    if (!projectId) throw new Error("Missing project id");
    const payload = { name: title.trim(), project_id: projectId };
    const res = await put<any>(`descriptor-set/${id}`, payload, {});
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    await dispatch(fetchSets());
    return res.data;
  },
);

export const deleteSet = createAsyncThunk(
  "descriptors/deleteSet",
  async (id: string, { dispatch }) => {
    const res = await remove(`descriptor-set/${id}`);
    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");
    await dispatch(fetchSets());
    return id;
  },
);

/* ----------------------- Fields ----------------------- */

export const fetchFields = createAsyncThunk(
  "descriptors/fetchFields",
  async (setId: string) => {
    const res = await get<any>("/descriptor-fields", {
      params: { descriptor_set_id: setId },
    });
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    const list: any[] = Array.isArray(res.data)
      ? res.data
      : res.data?.items || [];

    const mapped: DescriptorField[] = list.map((it: any) => ({
      id: it.id,
      name: it.title ?? it.name,
      type: it.type,
      is_dynamic: it.is_dynamic,
      descriptor_set_id: it.descriptor_set_id,
      optionsFull: Array.isArray(it.options)
        ? it.options.map((o: any) => ({ id: o.id, option: o.option }))
        : undefined,
    }));
    return mapped;
  },
);

export const fetchFieldOptions = createAsyncThunk(
  "descriptors/fetchFieldOptions",
  async (_: void, { getState }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const optionFields = s.fields.items.filter((f) => f.type === "option_list");
    const out: Record<string, Array<{ id: string; option: string }>> = {};
    for (const f of optionFields) {
      try {
        const r = await get<any>(`descriptor-field/${f.id}`, {});
        if (r.type === "success" && Array.isArray(r.data?.options)) {
          out[f.id] = r.data.options.map((o: any) => ({
            id: o.id,
            option: o.option,
          }));
        }
      } catch {
        // ignore
      }
    }
    return out;
  },
);

export const fetchFieldDetail = createAsyncThunk(
  "descriptors/fetchFieldDetail",
  async (fieldId: string) => {
    const res = await get<any>(`descriptor-field/${fieldId}`, {});
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    const d = res.data;
    return {
      detail: d,
      initialValues: {
        name: d.title ?? d.name,
        type: d.type,
        is_dynamic: !!d.is_dynamic,
        description: d.description ?? "",
        options:
          d.type === "option_list"
            ? (normalizeOptions(d.options) ?? [])
            : undefined,
      },
    };
  },
);

export const createField = createAsyncThunk(
  "descriptors/createField",
  async (
    values: {
      name: string;
      type: FieldType;
      is_dynamic: boolean;
      description?: string;
      options?: string[];
    },
    { getState, dispatch },
  ) => {
    const { selectedSetId } = (getState() as any)
      .descriptors as DescriptorsSliceState;
    if (!selectedSetId) throw new Error("No set selected");

    const base: any = {
      title: values.name?.trim(),
      description: values.description ?? "",
      type: values.type,
      is_dynamic: values.is_dynamic,
      descriptor_set_id: selectedSetId,
    };

    const optionsList =
      values.type === "option_list"
        ? (values.options || []).map((s) => s.trim()).filter(Boolean)
        : [];

    const candidates: any[] = [
      optionsList.length ? { ...base, options: optionsList } : base,
      optionsList.length
        ? { ...base, options: optionsList.map((o) => ({ option: o })) }
        : base,
      optionsList.length ? { ...base, new_options: optionsList } : base,
    ];

    let lastError = "Failed to create field";
    for (const payload of candidates) {
      const res = await post<any>("descriptor-field/create", payload, {});
      if (res.type === "success") {
        await dispatch(fetchFields(selectedSetId));
        return res.data;
      }
      lastError = res?.data?.message || lastError;
    }
    throw new Error(lastError);
  },
);

export const updateField = createAsyncThunk(
  "descriptors/updateField",
  async (
    values: {
      id: string;
      name: string;
      type: FieldType;
      is_dynamic: boolean;
      description?: string;
      options?: string[];
    },
    { getState, dispatch },
  ) => {
    const { selectedSetId, fields } = (getState() as any)
      .descriptors as DescriptorsSliceState;
    if (!selectedSetId) throw new Error("No set selected");

    const payload: any = {
      title: values.name?.trim(),
      description: values.description ?? "",
      type: values.type,
      is_dynamic: values.is_dynamic,
      descriptor_set_id: selectedSetId,
    };

    if (values.type === "option_list") {
      const prev = fields.editingDetail?.options as
        | Array<{ id: string; option: string }>
        | undefined;

      const { keepOrUpdate, newOnes } = splitOptionsForUpdate(
        values.options,
        prev,
      );

      if (keepOrUpdate.length) payload.options = keepOrUpdate;
      if (newOnes.length) payload.new_options = newOnes;
    }

    const res = await put<any>(`descriptor-field/${values.id}`, payload, {});
    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    await dispatch(fetchFields(selectedSetId));
    return res.data;
  },
);

export const deleteField = createAsyncThunk(
  "descriptors/deleteField",
  async (id: string, { getState, dispatch }) => {
    const res = await remove(`descriptor-field/${id}`);
    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");
    const { selectedSetId } = (getState() as any)
      .descriptors as DescriptorsSliceState;
    if (selectedSetId) await dispatch(fetchFields(selectedSetId));
    return id;
  },
);

/* ----------------------- Descriptors List ----------------------- */

export const fetchDescriptors = createAsyncThunk(
  "descriptors/fetchDescriptors",
  async (_: void, { getState }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const setId = s.selectedSetId;
    if (!setId) throw new Error("No set selected");

    const { page, pageSize } = s.descriptors;

    const res = await get<any>("/descriptors", {
      params: {
        descriptor_set_id: setId,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      },
    });

    if (res?.type && res.type !== "success") {
      throw new Error(res.data?.message || "Failed");
    }

    const outer = res?.data ?? res;
    let payload: any;

    if (
      outer &&
      typeof outer === "object" &&
      "data" in outer &&
      "total" in outer
    ) {
      payload = outer;
    } else if (
      outer &&
      typeof outer === "object" &&
      "data" in outer &&
      outer.data &&
      typeof outer.data === "object" &&
      "data" in outer.data
    ) {
      payload = outer.data;
    } else {
      payload = Array.isArray(outer)
        ? { data: outer, total: outer.length }
        : { data: [], total: 0 };
    }

    const rows: any[] = Array.isArray(payload.data) ? payload.data : [];
    const total: number =
      typeof payload.total === "number" ? payload.total : rows.length;

    const items: DescriptorRow[] = rows.map((r: any) => {
      const values: Record<string, any> = {};
      (r.values || []).forEach((v: any) => {
        const key = v.field_id;
        const display =
          v.type === "option_list"
            ? (v.field_option_value ?? null)
            : (v.dynamic_value ?? null);

        if (display === null || display === undefined) return;

        if (values[key] === undefined) values[key] = display;
        else if (Array.isArray(values[key])) values[key].push(display);
        else values[key] = [values[key], display];
      });

      const titles = (Array.isArray(r.linked_media) ? r.linked_media : [])
        .map((m: any) => m?.title ?? m?.name ?? m?.media_title ?? "")
        .filter(Boolean)
        .join(", ");

      return {
        id: r.id,
        weight: r.weight ?? null,
        is_promoted: r.is_promoted ?? false,
        promotion_id: r.promotion_id ?? null,
        linked_media: r.linked_media_count ?? 0,
        media_titles: titles || "",
        values,
      };
    });

    return { items, total };
  },
);

//Get a single Descriptor (id)
export const fetchDescriptorById = createAsyncThunk(
  "descriptors/fetchDescriptorById",
  async (id: string) => {
    const res = await get<any>(`/descriptor/${id}`, {});
    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");

    const d = res.data ?? res;

    // Mapo vlerat në shape-in e formës: { [field_id]: value | valueId[] }
    const formValues: Record<string, any> = {};
    const byField: Record<string, any[]> = {};

    (Array.isArray(d.values) ? d.values : []).forEach((v: any) => {
      const fid = v.field_id;
      const isOption = v.type === "option_list";
      const value = isOption ? v.field_options_id : v.dynamic_value;

      if (value === null || value === undefined || value === "") return;

      if (!byField[fid]) byField[fid] = [];
      byField[fid].push(value);
    });

    Object.entries(byField).forEach(([fid, arr]) => {
      // nëse ka 1 value, ktheje si string/primitive; nëse disa, si array
      formValues[fid] = arr.length === 1 ? arr[0] : arr;
    });

    // Nëse do ta shfaqësh “Media title” (jo detyrimisht editable)
    const mediaTitles = (Array.isArray(d.linked_media) ? d.linked_media : [])
      .map((m: any) => m?.media_title || m?.title || m?.name || "")
      .filter(Boolean)
      .join(", ");

    formValues.weight = d.weight ?? 0;

    return {
      id: d.id,
      formValues,
      mediaTitles,
      raw: d,
    };
  },
);

// Create a Descriptor (values)
export const createDescriptor = createAsyncThunk(
  "/descriptors/createDescriptor",
  async (formValues: Record<string, any>, { getState, dispatch }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const setId = s.selectedSetId;
    if (!setId) throw new Error("No set selected");

    const values: any[] = [];
    for (const f of s.fields.items) {
      const v = formValues[f.id];
      if (v === undefined || v === null || v === "") continue;

      if (f.type === "option_list") {
        const ids = Array.isArray(v) ? v : [v];
        ids.forEach((optionId: string) =>
          values.push({ field_id: f.id, field_options_id: optionId }),
        );
      } else {
        values.push({ field_id: f.id, dynamic_value: v });
      }
    }

    const payload = {
      descriptor_set_id: setId,
      weight: formValues.weight ?? 0,
      values,
    };
    const res = await post<any>("descriptor/create/", payload, {});

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    await dispatch(fetchDescriptors());
    return res.data;
  },
);

/* ----------------------- NEW: Update/Delete Descriptor -------------------- */

// zëvendëso funksionin ekzistues updateDescriptor me këtë version:
export const updateDescriptor = createAsyncThunk(
  "descriptors/updateDescriptor",
  async (
    args: {
      id: string;
      formValues: Record<string, any>;
      /** nëse do të linkosh një dokument gjatë update-it */
      documentId?: string;
    },
    { getState, dispatch },
  ) => {
    const { id, formValues, documentId } = args;
    const s = (getState() as any).descriptors as DescriptorsSliceState;

    // ndërto "values" sipas tipeve të fushave
    const values: Array<{
      field_id: string;
      field_options_id?: string;
      dynamic_value?: any;
    }> = [];

    for (const f of s.fields.items) {
      const raw = formValues[f.id];
      if (raw === undefined || raw === null || raw === "") continue;

      if (f.type === "option_list") {
        // dërgo ID-të e opsioneve (një ose disa)
        const ids = Array.isArray(raw) ? raw : [raw];
        ids
          .filter(Boolean)
          .forEach((optionId: string) =>
            values.push({ field_id: f.id, field_options_id: optionId }),
          );
      } else {
        // text/number/date – dërgo dynamic_value
        values.push({ field_id: f.id, dynamic_value: raw });
      }
    }

    // ndërto payload-in sipas swagger-it të ri
    const payload: any = {
      weight: formValues.weight ?? 0,
      values,
    };

    if (documentId) payload.document_id = documentId;

    // ENDPOINT i ri: /descriptors/{descriptor_id}
    const res = await put<any>(`/descriptors/${id}`, payload, {});
    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");

    await dispatch(fetchDescriptors());
    return res.data;
  },
);

/* ----------------------- Linked Media (documents API) --------------------- */
/** Mapper i përbashkët për rreshtat e modalit */
const mapDocToMediaRow = (doc: any): MediaRow => ({
  id: doc.document_id ?? doc.id,
  linkedMediaId: doc.linked_media_id ?? doc.link_id ?? doc.relation_id ?? null,
  username: doc.uploaded_by ?? doc.username ?? "—",
  documentName: doc.document_name ?? doc.title ?? doc.name ?? "(untitled)",
  documentType:
    doc.document_type === "internal"
      ? "Internal"
      : doc.document_type === "external"
        ? "External"
        : doc.document_type,
  codes: Number(doc.num_codes ?? doc.codes ?? 0),
  date: String(doc.uploaded_at || doc.created_at || "").slice(0, 10),
  highlightLength: Number(
    doc.highlight_length_count ?? doc.highlight_length ?? 0,
  ),
  createdBy: doc.created_by ?? "—",
  modifiedBy: doc.modified_by ?? "—",
});

// LINKED: project_id + descriptor_id + is_linked=true
export const fetchLinkedMedia = createAsyncThunk(
  "descriptors/fetchLinkedMedia",
  async (_: void, { getState }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const dId = s.modals.linkedMedia.descriptorId;
    const pId = s.modals.linkedMedia.projectId || s.projectId;
    const { page, pageSize } = s.modals.linkedMedia.linked;

    if (!pId || !dId) throw new Error("Missing projectId/descriptorId");

    const res = await get<any>("/documents", {
      params: {
        project_id: pId,
        descriptor_id: dId,
        is_linked: true,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      },
      headers: { Accept: "application/json" },
    });

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const payload = res?.data ?? res;
    const list: any[] = payload?.items ?? payload?.data ?? [];
    const total: number =
      typeof payload?.total === "number" ? payload.total : list.length;

    const items: MediaRow[] = list.map(mapDocToMediaRow);
    return { items, total };
  },
);

// UNLINKED: vetëm project_id (jo descriptor_id, jo is_linked)
// export const fetchUnlinkedMedia = createAsyncThunk(
//   "descriptors/fetchUnlinkedMedia",
//   async (_: void, { getState }) => {
//     const s = (getState() as any).descriptors as DescriptorsSliceState;
//     const pId = s.modals.linkedMedia.projectId || s.projectId;
//     const { page, pageSize } = s.modals.linkedMedia.unlinked;

//     if (!pId) throw new Error("Missing projectId");

//     const res = await get<any>("/documents", {
//       params: {
//         project_id: pId,
//         skip: (page - 1) * pageSize,
//         limit: pageSize,
//       },
//       headers: { Accept: "application/json" },
//     });

//     const payload = res?.data ?? res;
//     const list: any[] = payload?.items ?? payload?.data ?? [];
//     const total: number =
//       typeof payload?.total === "number" ? payload.total : list.length;

//     const items: MediaRow[] = list.map(mapDocToMediaRow);
//     return { items, total };
//   }
// );

// UNLINKED: project_id + descriptor_id + is_linked=false
export const fetchUnlinkedMedia = createAsyncThunk(
  "descriptors/fetchUnlinkedMedia",
  async (_: void, { getState }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const dId = s.modals.linkedMedia.descriptorId;
    const pId = s.modals.linkedMedia.projectId || s.projectId;
    const { page, pageSize } = s.modals.linkedMedia.unlinked;

    if (!pId || !dId) throw new Error("Missing projectId/descriptorId");

    const res = await get<any>("/documents", {
      params: {
        project_id: pId,
        descriptor_id: dId,
        is_linked: false,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      },
      headers: { Accept: "application/json" },
    });

    const payload = res?.data ?? res;
    const list: any[] = payload?.items ?? payload?.data ?? [];
    const total: number =
      typeof payload?.total === "number" ? payload.total : list.length;

    const items: MediaRow[] = list.map(mapDocToMediaRow);
    return { items, total };
  },
);

export const linkMedia = createAsyncThunk(
  "descriptors/linkMedia",
  async (mediaId: string, { getState, dispatch }) => {
    const s = (getState() as any).descriptors as DescriptorsSliceState;
    const dId = s.modals.linkedMedia.descriptorId!;
    // NEW endpoint + payload shape
    const res = await post<any>(
      `/linked-media`,
      { descriptors_id: dId, media_id: mediaId },
      {},
    );

    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");

    // refresh both tabs and the descriptor list
    await dispatch(fetchLinkedMedia());
    await dispatch(fetchUnlinkedMedia());
    await dispatch(fetchDescriptors());
    return true;
  },
);

export const unlinkMedia = createAsyncThunk(
  "descriptors/unlinkMedia",
  async (linkedMediaId: string, { dispatch }) => {
    const res = await remove(`/linked-media/${linkedMediaId}`);
    if (res?.type === "error") throw new Error(res?.data?.message || "Failed");

    await dispatch(fetchLinkedMedia());
    await dispatch(fetchUnlinkedMedia());
    await dispatch(fetchDescriptors());
    return true;
  },
);

export const deleteDescriptor = createAsyncThunk(
  "descriptors/deleteDescriptor",
  async (id: string, { dispatch }) => {
    const headers = { Accept: "application/json" };

    let res = await remove(`/descriptor/${id}/`, { headers } as any);

    if (res?.type === "error")
      res = await remove(`/descriptor/${id}`, { headers } as any);
    if (res?.type === "error")
      res = await remove(`/descriptors/${id}/`, { headers } as any);
    if (res?.type === "error")
      res = await remove(`/descriptors/${id}`, { headers } as any);

    if (res?.type === "error") {
      throw new Error(res?.data?.message || "Failed");
    }

    await dispatch(fetchDescriptors());
    return id;
  },
);

/* ------------------------------- Initial ---------------------------------- */

const initialPaged = <T>(): Paged<T> => ({
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
});

const initialState: DescriptorsSliceState = {
  projectId: null,
  selectedSetId: null,
  submitting: false,

  sets: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    search: "",
    loading: false,
  },
  fields: { items: [], loading: false, editingDetail: null },
  descriptors: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 10,
    search: "",
    loading: false,
  },

  modals: {
    setOpen: false,
    setMode: "add",
    editingSet: null,

    fieldOpen: false,
    fieldMode: "add",
    editingField: null,
    fieldInitialValues: null,

    deleteSetOpen: false,
    deletingSet: null,

    deleteFieldOpen: false,
    deletingField: null,

    descriptorOpen: false,
    descriptorMode: "add",
    editingDescriptor: null,

    deleteDescriptorOpen: false,
    deletingDescriptor: null,
    descriptorInitialValues: null,
    descriptorInitialLabel: null,

    linkedMedia: {
      open: false,
      descriptorId: null,
      projectId: null, // NEW
      descriptorLabel: null,
      tab: "linked",
      linked: initialPaged<MediaRow>(),
      unlinked: initialPaged<MediaRow>(),
      mutatingId: null,
    },
  },
};

/* -------------------------------- Slice ----------------------------------- */

const descriptorsSlice = createSlice({
  name: "descriptors",
  initialState,
  reducers: {
    setProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
    },

    // selection
    selectSet(state, action: PayloadAction<string | null>) {
      state.selectedSetId = action.payload;
      state.fields.items = [];
      state.descriptors.items = [];
      state.descriptors.page = 1;
      state.descriptors.total = 0;
    },

    // sets paging/search
    setSetsPage(state, action: PayloadAction<number>) {
      state.sets.page = action.payload;
    },
    setSetsPageSize(state, action: PayloadAction<number>) {
      state.sets.pageSize = action.payload;
    },
    setSetsSearch(state, action: PayloadAction<string>) {
      state.sets.search = action.payload;
    },

    // descriptors paging/search
    setDescPage(state, action: PayloadAction<number>) {
      state.descriptors.page = action.payload;
    },
    setDescPageSize(state, action: PayloadAction<number>) {
      state.descriptors.pageSize = action.payload;
    },
    setDescSearch(state, action: PayloadAction<string>) {
      state.descriptors.search = action.payload;
    },

    // sets modals
    openSetModal(state, action: PayloadAction<"add" | "edit">) {
      state.modals.setMode = action.payload;
      state.modals.setOpen = true;
    },
    closeSetModal(state) {
      state.modals.setOpen = false;
      state.modals.editingSet = null;
    },
    setEditingSet(state, action: PayloadAction<DescriptorSet | null>) {
      state.modals.editingSet = action.payload;
    },

    openDeleteSet(state, action: PayloadAction<DescriptorSet>) {
      state.modals.deletingSet = action.payload;
      state.modals.deleteSetOpen = true;
    },
    closeDeleteSet(state) {
      state.modals.deletingSet = null;
      state.modals.deleteSetOpen = false;
    },

    // field modals
    openFieldModal(state, action: PayloadAction<"add" | "edit">) {
      state.modals.fieldMode = action.payload;
      state.modals.fieldOpen = true;
    },
    closeFieldModal(state) {
      state.modals.fieldOpen = false;
      state.modals.editingField = null;
      state.modals.fieldInitialValues = null;
      state.fields.editingDetail = null;
    },
    setEditingField(state, action: PayloadAction<DescriptorField | null>) {
      state.modals.editingField = action.payload;
    },

    // descriptor (values) modal
    openDescriptorModal(state, action: PayloadAction<"add" | "edit">) {
      state.modals.descriptorMode = action.payload;
      state.modals.descriptorOpen = true;
    },
    closeDescriptorModal(state) {
      state.modals.descriptorOpen = false;
      state.modals.editingDescriptor = null;
      state.modals.descriptorInitialValues = null;
      state.modals.descriptorInitialLabel = null;
    },
    setEditingDescriptor(state, action: PayloadAction<DescriptorRow | null>) {
      state.modals.editingDescriptor = action.payload;
    },

    // delete field modal
    openDeleteField(
      state,
      action: PayloadAction<{ id: string; name: string }>,
    ) {
      state.modals.deleteFieldOpen = true;
      state.modals.deletingField = action.payload;
    },
    closeDeleteField(state) {
      state.modals.deleteFieldOpen = false;
      state.modals.deletingField = null;
    },

    /* ---- NEW: descriptor delete modal controls ---- */
    openDeleteDescriptor(
      state,
      action: PayloadAction<{ id: string; title?: string }>,
    ) {
      state.modals.deleteDescriptorOpen = true;
      state.modals.deletingDescriptor = action.payload;
    },
    closeDeleteDescriptor(state) {
      state.modals.deleteDescriptorOpen = false;
      state.modals.deletingDescriptor = null;
    },

    /* ---- Linked media modal controls ---- */
    openLinkedMediaModal(
      state,
      action: PayloadAction<{
        descriptorId: string;
        projectId: string;
        label?: string;
      }>,
    ) {
      state.modals.linkedMedia.open = true;
      state.modals.linkedMedia.descriptorId = action.payload.descriptorId;
      state.modals.linkedMedia.projectId = action.payload.projectId; // NEW
      state.modals.linkedMedia.descriptorLabel = action.payload.label ?? null;
      state.modals.linkedMedia.tab = "linked";
      state.modals.linkedMedia.linked.page = 1;
      state.modals.linkedMedia.unlinked.page = 1;
    },
    closeLinkedMediaModal(state) {
      state.modals.linkedMedia.open = false;
      state.modals.linkedMedia.descriptorId = null;
      state.modals.linkedMedia.projectId = null;
    },
    setLinkedTab(state, action: PayloadAction<"linked" | "unlinked">) {
      state.modals.linkedMedia.tab = action.payload;
    },
    setLinkedPage(state, action: PayloadAction<number>) {
      state.modals.linkedMedia.linked.page = action.payload;
    },
    setLinkedPageSize(state, action: PayloadAction<number>) {
      state.modals.linkedMedia.linked.pageSize = action.payload;
    },
    setUnlinkedPage(state, action: PayloadAction<number>) {
      state.modals.linkedMedia.unlinked.page = action.payload;
    },
    setUnlinkedPageSize(state, action: PayloadAction<number>) {
      state.modals.linkedMedia.unlinked.pageSize = action.payload;
    },
  },
  extraReducers: (b) => {
    /* Sets */
    b.addCase(fetchSets.pending, (s) => {
      s.sets.loading = true;
    });
    b.addCase(fetchSets.fulfilled, (s, a) => {
      s.sets.loading = false;
      s.sets.items = a.payload.items || [];
      s.sets.total = a.payload.total || 0;
    });
    b.addCase(fetchSets.rejected, (s) => {
      s.sets.loading = false;
    });

    b.addCase(createSet.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(createSet.fulfilled, (s) => {
      s.submitting = false;
      s.modals.setOpen = false;
      s.sets.page = 1;
    });
    b.addCase(createSet.rejected, (s) => {
      s.submitting = false;
    });

    b.addCase(updateSet.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(updateSet.fulfilled, (s) => {
      s.submitting = false;
      s.modals.setOpen = false;
    });
    b.addCase(updateSet.rejected, (s) => {
      s.submitting = false;
    });

    b.addCase(deleteSet.fulfilled, (s, a) => {
      if (s.selectedSetId === a.payload) s.selectedSetId = null;
      s.modals.deleteSetOpen = false;
      s.modals.deletingSet = null;
    });

    /* Fields */
    b.addCase(fetchFields.pending, (s) => {
      s.fields.loading = true;
    });
    b.addCase(fetchFields.fulfilled, (s, a) => {
      s.fields.loading = false;
      s.fields.items = a.payload;
    });
    b.addCase(fetchFields.rejected, (s) => {
      s.fields.loading = false;
    });

    b.addCase(fetchFieldDetail.fulfilled, (s, a) => {
      s.fields.editingDetail = a.payload.detail;
      s.modals.fieldInitialValues = a.payload.initialValues;
    });

    b.addCase(createField.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(createField.fulfilled, (s) => {
      s.submitting = false;
      s.modals.fieldOpen = false;
    });
    b.addCase(createField.rejected, (s) => {
      s.submitting = false;
    });

    b.addCase(updateField.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(updateField.fulfilled, (s) => {
      s.submitting = false;
      s.modals.fieldOpen = false;
      s.modals.editingField = null;
      s.fields.editingDetail = null;
    });
    b.addCase(updateField.rejected, (s) => {
      s.submitting = false;
    });

    b.addCase(deleteField.fulfilled, (s) => {
      s.modals.deleteFieldOpen = false;
      s.modals.deletingField = null;
    });

    /* Descriptors */
    b.addCase(fetchDescriptors.pending, (s) => {
      s.descriptors.loading = true;
    });
    b.addCase(fetchDescriptors.fulfilled, (s, a) => {
      s.descriptors.loading = false;
      s.descriptors.items = a.payload.items;
      s.descriptors.total = a.payload.total;
    });
    b.addCase(fetchDescriptors.rejected, (s) => {
      s.descriptors.loading = false;
    });

    b.addCase(createDescriptor.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(createDescriptor.fulfilled, (s) => {
      s.submitting = false;
      s.modals.descriptorOpen = false;
    });
    b.addCase(createDescriptor.rejected, (s) => {
      s.submitting = false;
    });

    // NEW: update/delete descriptor
    b.addCase(updateDescriptor.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(updateDescriptor.fulfilled, (s) => {
      s.submitting = false;
      s.modals.descriptorOpen = false;
      s.modals.editingDescriptor = null;
    });
    b.addCase(updateDescriptor.rejected, (s) => {
      s.submitting = false;
    });

    b.addCase(deleteDescriptor.fulfilled, (s) => {
      s.modals.deleteDescriptorOpen = false;
      s.modals.deletingDescriptor = null;
    });

    /* Linked media */
    b.addCase(fetchLinkedMedia.pending, (s) => {
      s.modals.linkedMedia.linked.loading = true;
    });
    b.addCase(fetchLinkedMedia.fulfilled, (s, a) => {
      s.modals.linkedMedia.linked.loading = false;
      s.modals.linkedMedia.linked.items = a.payload.items;
      s.modals.linkedMedia.linked.total = a.payload.total;
    });
    b.addCase(fetchLinkedMedia.rejected, (s) => {
      s.modals.linkedMedia.linked.loading = false;
    });

    b.addCase(fetchUnlinkedMedia.pending, (s) => {
      s.modals.linkedMedia.unlinked.loading = true;
    });
    b.addCase(fetchUnlinkedMedia.fulfilled, (s, a) => {
      s.modals.linkedMedia.unlinked.loading = false;
      s.modals.linkedMedia.unlinked.items = a.payload.items;
      s.modals.linkedMedia.unlinked.total = a.payload.total;
    });
    b.addCase(fetchUnlinkedMedia.rejected, (s) => {
      s.modals.linkedMedia.unlinked.loading = false;
    });

    b.addCase(linkMedia.pending, (s, a) => {
      s.modals.linkedMedia.mutatingId = a.meta.arg as string;
    });
    b.addCase(linkMedia.fulfilled, (s) => {
      s.modals.linkedMedia.mutatingId = null;
    });
    b.addCase(linkMedia.rejected, (s) => {
      s.modals.linkedMedia.mutatingId = null;
    });

    b.addCase(unlinkMedia.pending, (s, a) => {
      s.modals.linkedMedia.mutatingId = a.meta.arg as string;
    });
    b.addCase(unlinkMedia.fulfilled, (s) => {
      s.modals.linkedMedia.mutatingId = null;
    });
    b.addCase(unlinkMedia.rejected, (s) => {
      s.modals.linkedMedia.mutatingId = null;
    });
    b.addCase(fetchDescriptorById.pending, (s) => {
      s.submitting = true;
    });
    b.addCase(fetchDescriptorById.fulfilled, (s, a) => {
      s.submitting = false;
      s.modals.descriptorInitialValues = a.payload.formValues;
      s.modals.descriptorInitialLabel = a.payload.mediaTitles || null;
    });
    b.addCase(fetchDescriptorById.rejected, (s) => {
      s.submitting = false;
      s.modals.descriptorInitialValues = null;
      s.modals.descriptorInitialLabel = null;
    });
  },
});

export const {
  setProjectId,
  selectSet,
  // sets paging/search
  setSetsPage,
  setSetsPageSize,
  setSetsSearch,
  // descriptors paging/search
  setDescPage,
  setDescPageSize,
  setDescSearch,
  // set modals
  openSetModal,
  closeSetModal,
  setEditingSet,
  openDeleteSet,
  closeDeleteSet,
  // field modals
  openFieldModal,
  closeFieldModal,
  setEditingField,
  openDeleteField,
  closeDeleteField,
  // descriptor (values) modal
  openDescriptorModal,
  closeDescriptorModal,
  setEditingDescriptor,
  // NEW: descriptor delete modal
  openDeleteDescriptor,
  closeDeleteDescriptor,
  // linked media modal
  openLinkedMediaModal,
  closeLinkedMediaModal,
  setLinkedTab,
  setLinkedPage,
  setLinkedPageSize,
  setUnlinkedPage,
  setUnlinkedPageSize,
} = descriptorsSlice.actions;

export default descriptorsSlice.reducer;
